import { locateNode, toNode } from "@dhx/ts-common/html";
import { IPosition, ITooltipConfig, Position, RealPosition } from "./types";

const DEFAULT_SHOW_DELAY = 750;
const DEFAULT_HIDE_DELAY = 200;

export function findPosition(
	targetRect: ClientRect,
	position: Position,
	width: number,
	height: number
): IPosition {
	const margin = 8; // margin top/bot, left/right

	let pos: RealPosition;
	let left: number;
	let top: number;

	switch(position) {
		case Position.center:
			left = targetRect.left + window.pageXOffset + (targetRect.width - width) / 2;
			if (left + margin < window.pageXOffset) {
				left = targetRect.left + window.pageXOffset;
			}
			top = targetRect.top + window.pageYOffset + (targetRect.height - height) / 2;
			pos = RealPosition.center;
			return {left, top, pos};
		case Position.right:
			pos = RealPosition.right;
			left = targetRect.right + window.pageXOffset;
			if (left + width + margin > window.innerWidth + window.pageXOffset) { // set left
				left = window.pageXOffset + targetRect.left - width;
				pos = RealPosition.left;
			}
			top = window.pageYOffset + targetRect.top + (targetRect.height - height) / 2;
			return {left, top, pos};
		case Position.bottom:
		default:
			left = window.pageXOffset + targetRect.left + (targetRect.width - width) / 2;
			if (left + width > window.innerWidth + window.pageXOffset) {
				left = window.innerWidth + window.pageXOffset - width;
			} else if (left < 0) {
				left = 0;
			}
			pos = RealPosition.bottom;
			top = window.pageYOffset + targetRect.bottom;
			if (top + height + margin > window.innerHeight + window.pageYOffset) { // set top
				top = window.pageYOffset + targetRect.top - height;
				pos = RealPosition.top;
			}
			return {left, top, pos};
	}
}


// tooltip init
const tooltipBox = document.createElement("div");
const tooltipText = document.createElement("span");
tooltipText.className = "dhx_tooltip__text";
tooltipBox.appendChild(tooltipText);
tooltipBox.style.position = "absolute";

let lastNode = null;
let isActive = false;
let hideTimeout = null;
let showTimeout = null;

let activeListenersDestructor;

function showTooltip(node: Element, text: string, position: Position, css: string, force = false) {
	const rects: ClientRect = node.getBoundingClientRect();
	tooltipText.textContent = text;
	document.body.appendChild(tooltipBox);
	tooltipBox.className = "dhx_widget dhx_tooltip" + (force ? " dhx_tooltip--forced" : "");
	const {width, height} = tooltipBox.getBoundingClientRect();
	const {left, top, pos} = findPosition(rects, position, width, height);
	switch(pos) {
		case RealPosition.bottom:
			tooltipBox.style.left = left + "px";
			tooltipBox.style.top = top + "px";
			break;

		case RealPosition.top:
			tooltipBox.style.left = left + "px";
			tooltipBox.style.top = top + "px";
			break;

		case RealPosition.left:
			tooltipBox.style.left = left + "px";
			tooltipBox.style.top = top + "px";
			break;

		case RealPosition.right:
			tooltipBox.style.left = left + "px";
			tooltipBox.style.top = top + "px";
			break;

		case RealPosition.center:
			tooltipBox.style.left = left + "px";
			tooltipBox.style.top = top + "px";
			break;
	}
	tooltipBox.className += ` dhx_tooltip--${pos} ${(css || "")}`;

	isActive = true;
	if (!force) {
		setTimeout(() => {
			tooltipBox.className += " dhx_tooltip--animate";
		});
	}
}
function hideTooltip(delay: number) {
	if (lastNode) {
		hideTimeout = setTimeout(() => {
			document.body.removeChild(tooltipBox);
			isActive = false;
			hideTimeout = null;
		}, delay || DEFAULT_HIDE_DELAY);
	}
}

function addListeners(node: HTMLElement, text: string, config: ITooltipConfig) {
	const {force, showDelay, hideDelay, position, css} = config;
	if (!force) {
		showTimeout = setTimeout(() => {
			showTooltip(node, text, position || Position.bottom, css);
		}, showDelay || DEFAULT_SHOW_DELAY);
	}
	const hide = function() {
		if (isActive) {
			hideTooltip(hideDelay);
		}
		clearTimeout(showTimeout);
		node.removeEventListener("mouseleave", hide);
		node.removeEventListener("blur", hide);
		document.removeEventListener("mousedown", hide);
		lastNode = null;
		activeListenersDestructor = null;
	};
	if (force) {
		showTooltip(node, text,position, css, force);
	}
	node.addEventListener("mouseleave", hide);
	node.addEventListener("blur", hide);
	document.addEventListener("mousedown", hide);

	activeListenersDestructor = hide;
}


// default
export function tooltip(text: string, config: ITooltipConfig) {
	const node = toNode(config.node);
	if (node === lastNode) {
		return;
	}

	if (activeListenersDestructor) {
		activeListenersDestructor();
		activeListenersDestructor = null;
	}

	lastNode = node;
	if (hideTimeout) {
		clearTimeout(hideTimeout);
		hideTimeout = null;
		addListeners(node, text, {...config, force: true});
	} else {
		addListeners(node, text, config);
	}
}

export function enableTooltip() {
	document.addEventListener("mousemove", _mousemove);
}
export function disableTooltip() {
	document.removeEventListener("mousemove", _mousemove);
}

function _mousemove(e: MouseEvent) {
	const node = locateNode(e, "dhx_tooltip_text");
	if (!node) {
		return;
	}

	tooltip(node.getAttribute("dhx_tooltip_text"), {
		position: node.getAttribute("dhx_tooltip_position") as Position || Position.bottom,
		node: node as HTMLElement
	});
}

